import React from 'react';
import { Activity } from 'lucide-react';

// Small sizes: plain border spinner (too small for icon inside)
// Medium+ sizes: border spinner with Activity heartbeat icon pulsing in center
const sizeMap = {
  xs: { wh: 'w-3.5 h-3.5', border: 'border-2', icon: false },
  sm: { wh: 'w-[18px] h-[18px]', border: 'border-2', icon: false },
  md: { wh: 'w-8 h-8', border: 'border-[3px]', icon: true, iconSize: 14 },
  lg: { wh: 'w-12 h-12', border: 'border-[3px]', icon: true, iconSize: 20 },
  xl: { wh: 'w-16 h-16', border: 'border-4', icon: true, iconSize: 24 },
} as const;

interface LoadingSpinnerProps {
  /** Size of the spinner - named size or pixel number */
  size?: keyof typeof sizeMap | number;
  /** Additional CSS classes */
  className?: string;
  /** Optional label to display below spinner */
  label?: string;
}

/**
 * Reusable loading spinner component
 * Small: CSS border spinner. Medium+: spinning ring with pulsing Activity icon
 *
 * @example
 * <LoadingSpinner size="lg" label="Loading..." />
 *
 * @example
 * <LoadingSpinner size={32} />
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  className = '',
  label,
}) => {
  if (typeof size === 'number') {
    const showIcon = size >= 28;
    return (
      <div className={`flex flex-col items-center justify-center gap-2 ${className}`}>
        <div className="relative">
          <div
            className="border-accent/20 border-t-accent rounded-full animate-spin"
            style={{ width: size, height: size, borderWidth: size > 24 ? 3 : 2 }}
          />
          {showIcon && (
            <Activity
              size={Math.round(size * 0.4)}
              className="absolute inset-0 m-auto text-accent animate-pulse"
            />
          )}
        </div>
        {label && <span className="text-sm text-slate-400">{label}</span>}
      </div>
    );
  }

  const s = sizeMap[size];
  return (
    <div className={`flex flex-col items-center justify-center gap-2 ${className}`}>
      {s.icon ? (
        <div className="relative">
          <div className={`${s.wh} ${s.border} border-accent/20 border-t-accent rounded-full animate-spin`} />
          <Activity
            size={s.iconSize}
            className="absolute inset-0 m-auto text-accent animate-pulse"
          />
        </div>
      ) : (
        <div className={`${s.wh} ${s.border} border-slate-700 border-t-accent rounded-full animate-spin`} />
      )}
      {label && <span className="text-sm text-slate-400">{label}</span>}
    </div>
  );
};

interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  isLoading: boolean;
  /** Optional label to display */
  label?: string;
  /** Children to render behind the overlay */
  children: React.ReactNode;
}

/**
 * Loading overlay that covers its children with a spinner
 * Useful for sections that need to show loading state
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  label,
  children,
}) => {
  return (
    <div className="relative">
      {children}
      {isLoading && (
        <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
          <LoadingSpinner size="lg" label={label} />
        </div>
      )}
    </div>
  );
};

interface FullPageLoaderProps {
  /** Optional label to display */
  label?: string;
}

/**
 * Full-page loading spinner
 * Used for initial app load or major transitions
 */
export const FullPageLoader: React.FC<FullPageLoaderProps> = ({ label = 'Loading...' }) => {
  return (
    <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50">
      <div className="flex flex-col items-center gap-4">
        <LoadingSpinner size="xl" />
        <span className="text-lg text-slate-300 font-medium">{label}</span>
      </div>
    </div>
  );
};

export default LoadingSpinner;
